<script setup lang="ts">
import type { PlanName } from '~/composables/usePlanConfig'

const props = withDefaults(defineProps<{
  plans: { key: PlanName, title: string, description: string, features: string[] }[]
  billingCycle: 'monthly' | 'yearly'
  checkoutLoading: string | null
  getButtonLabel: (plan: PlanName) => string
  paymentMode?: 'onetime' | 'subscription'
}>(), {
  paymentMode: 'subscription'
})

const emit = defineEmits<{
  planAction: [plan: PlanName]
}>()

const authStore = useAuthStore()
const { formatPrice, getYearlySavings, getUpgradePrice } = useCurrency()

const isOnetime = computed(() => props.paymentMode === 'onetime')

const PLAN_RANK: Record<string, number> = { free: 0, pro: 1, elite: 2 }

const isProToEliteUpgrade = computed(() =>
  isOnetime.value && authStore.isLifetime && authStore.plan === 'pro'
)

function isPlanDisabled(plan: PlanName): boolean {
  if (!authStore.userId) return false
  if (plan === authStore.plan) return true
  if (isOnetime.value && authStore.isLifetime && PLAN_RANK[plan] < PLAN_RANK[authStore.plan]) return true
  return false
}

function getDisplayLabel(plan: PlanName): string {
  if (isOnetime.value && authStore.isLifetime && PLAN_RANK[plan] < PLAN_RANK[authStore.plan]) {
    return 'Already Higher'
  }
  if (plan === 'elite' && isProToEliteUpgrade.value) {
    return 'Upgrade to Elite'
  }
  return props.getButtonLabel(plan)
}

function isPlanHighlighted(plan: PlanName): boolean {
  if (authStore.plan === 'free') return plan === 'pro'
  if (authStore.plan === 'pro') return plan === 'elite'
  return false
}

function getPlanPrice(plan: PlanName): string {
  if (plan === 'elite' && isProToEliteUpgrade.value) {
    return getUpgradePrice('pro', 'elite')
  }
  return formatPrice(plan, isOnetime.value ? 'lifetime' : props.billingCycle)
}

function getBillingLabel(plan: PlanName): string {
  if (plan === 'free') return '/forever'
  if (isOnetime.value) return ' · one-time'
  return props.billingCycle === 'monthly' ? '/month' : '/year'
}

function getPlanYearlySavings(plan: PlanName): string | null {
  if (isOnetime.value) return null
  if (props.billingCycle !== 'yearly') return null
  return getYearlySavings(plan)
}
</script>

<template>
  <div class="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
    <div
      v-for="plan in plans"
      :key="plan.key"
      class="relative flex flex-col rounded-2xl border p-6 shadow-(--surface-shadow)"
      :class="isPlanHighlighted(plan.key)
        ? 'border-primary-400/40 bg-primary-500/4'
        : 'border-default bg-elevated/40'"
    >
      <!-- Plan name + badges -->
      <div class="flex items-center gap-2 mb-2">
        <UIcon
          v-if="plan.key === 'pro'"
          name="i-lucide-zap"
          class="size-4.5 text-fuchsia-400"
        />
        <UIcon
          v-if="plan.key === 'elite'"
          name="i-lucide-crown"
          class="size-4.5 text-warning-400"
        />
        <h3 class="text-lg font-bold text-highlighted">
          {{ plan.title }}
        </h3>
        <UBadge
          v-if="authStore.userId && plan.key === authStore.plan"
          :color="plan.key === 'free' ? 'neutral' : 'success'"
          variant="subtle"
          size="xs"
        >
          Current
        </UBadge>
        <UBadge
          v-if="isOnetime && plan.key !== 'free'"
          color="success"
          variant="subtle"
          size="xs"
        >
          Lifetime
        </UBadge>
        <UBadge
          v-if="isPlanHighlighted(plan.key)"
          color="primary"
          variant="subtle"
          size="xs"
          class="ml-auto"
        >
          {{ plan.key === 'pro' ? 'Most popular' : 'Recommended' }}
        </UBadge>
      </div>

      <p class="text-sm text-muted mb-5">
        {{ plan.description }}
      </p>

      <!-- Price -->
      <div class="flex items-baseline gap-1 mb-1">
        <span class="text-3xl font-bold text-highlighted">{{ getPlanPrice(plan.key) }}</span>
        <span class="text-sm text-dimmed">{{ getBillingLabel(plan.key) }}</span>
        <UBadge
          v-if="getPlanYearlySavings(plan.key)"
          color="success"
          variant="subtle"
          size="sm"
          class="ml-2"
        >
          {{ getPlanYearlySavings(plan.key) }}
        </UBadge>
      </div>
      <p
        v-if="plan.key === 'elite' && isProToEliteUpgrade"
        class="text-xs text-dimmed mb-5"
      >
        upgrade from Pro · full price {{ formatPrice('elite', 'lifetime') }}
      </p>
      <div
        v-else
        class="mb-5"
      />

      <!-- CTA button or current plan text -->
      <div
        v-if="authStore.userId && plan.key === authStore.plan"
        class="mb-6 flex items-center justify-center py-3 text-sm font-medium text-dimmed"
      >
        Current Plan
      </div>
      <UButton
        v-else
        :label="getDisplayLabel(plan.key)"
        :color="isPlanHighlighted(plan.key) ? 'primary' : 'neutral'"
        :variant="isPlanHighlighted(plan.key) ? 'solid' : 'outline'"
        :loading="checkoutLoading === plan.key"
        :disabled="isPlanDisabled(plan.key)"
        block
        size="lg"
        class="mb-6"
        @click="!isPlanDisabled(plan.key) && emit('planAction', plan.key)"
      />

      <!-- Features -->
      <ul class="flex flex-col gap-3 flex-1">
        <li
          v-for="(feature, i) in plan.features"
          :key="i"
          class="flex items-start gap-2.5 text-sm"
          :class="i === 0 && plan.key !== 'free' ? 'text-toned font-medium' : 'text-muted'"
        >
          <UIcon
            name="i-lucide-check"
            class="size-4 shrink-0 mt-0.5 text-primary-400/80"
          />
          <span>{{ feature }}</span>
        </li>
      </ul>

      <NuxtLink
        to="/pricing#compare"
        class="text-xs text-dimmed hover:text-muted transition-colors text-center mt-4"
      >
        Compare all features
      </NuxtLink>
    </div>
  </div>
</template>
